import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FolderCopyRoundedIcon from '@mui/icons-material/FolderCopyRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import DirectionsRunRoundedIcon from '@mui/icons-material/DirectionsRunRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import JiraProjectsTable, { projectHealthOf } from '../../../components/JiraProjectsTable';
import Footer from '../../../components/Footer';
import { ModelPageContext } from '../../../context/ModelPageContext';
import { usePageSearch } from '../../../context/PageSearchContext';
import CreateBacklogIssue from '../../../components/Create_Backlogissue';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../components/Toast';
import { notificationSubscription } from '../../../services/notificationSubscription';
import './landingprojects.scss';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const VIEW_MODES = [
  { key: 'list', label: 'List view', icon: <ViewListRoundedIcon /> },
  { key: 'tiles', label: 'Tiles view', icon: <GridViewRoundedIcon /> },
];
const VIEW_STORAGE_KEY = 'projam.projectsView';

/* the last view someone picked; storage can be blocked, so fall back to the list */
const readSavedView = () => {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'tiles' ? 'tiles' : 'list';
  } catch (error) {
    return 'list';
  }
};

const Index = () => {
  /* shared with the top bar, so either box filters this list */
  const [searchTerm, setSearchTerm] = usePageSearch('Search projects, keys or owners…');
  const [statusFilter, setStatusFilter] = useState('all');
  const [view, setView] = useState(readSavedView);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [issueModel, setIssueModel] = useState(false);
  const searchRef = useRef(null);
  const modalVerifyState = useContext(ModelPageContext);
  const navigate = useNavigate();
  const toast = useToast();
  const { Projects, error, status } = useSelector((state) => state.ProjamAppSlice);
  const projects = useMemo(() => (Array.isArray(Projects?.allProjectsList?.data) ? Projects.allProjectsList.data : []), [Projects?.allProjectsList]);

  useEffect(() => {
    notificationSubscription();
    if (error === '401' || error === '500') {
      localStorage.removeItem('accessToken');
      navigate('/login');
    }
  }, [error, navigate]);

  const counts = useMemo(
    () => ({
      total: projects.length,
      active: projects.filter((project) => project.active === 'true').length,
      inactive: projects.filter((project) => project.active !== 'true').length,
      sprinting: projects.filter((project) => project?.sprintInfo?._id).length,
      // overdue sprints need attention too, so they count here
      atRisk: projects.filter((project) => ['At Risk', 'Overdue'].includes(projectHealthOf(project.sprintInfo))).length,
    }),
    [projects]
  );

  const openCreateProject = () => {
    modalVerifyState.setModalState({ open: true, createProject: true });
  };

  const handleViewReports = () => {
    if (!selectedProjectIds.length) {
      toast.info('Select one or more projects first');
      return;
    }
    // ids also go in the URL so the report survives a refresh and can be shared
    navigate(`/projects-reports?projects=${selectedProjectIds.join(',')}`, { state: { projectIds: selectedProjectIds } });
  };

  const changeView = (next) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch (error) {
      /* only a convenience — the view still switches */
    }
  };

  const selectedCount = selectedProjectIds.length;

  return (
    <div className="projects-landing">
      <div className="pl-page">
        {/* -------------------------------------------------------------- header */}
        <section className="pl-hero">
          <div className="pl-hero__main">
            <span className="pl-hero__tile" aria-hidden="true">
              <FolderCopyRoundedIcon />
            </span>
            <div className="pl-hero__heading">
              <span className="pl-eyebrow">Workspace</span>
              <h1 className="pl-title">All projects</h1>
              <p className="pl-subtitle">Every project you have access to — open one, or select a few to compare.</p>
            </div>
            <Button className="pl-btn pl-btn--primary pl-hero__cta" startIcon={<AddRoundedIcon />} onClick={openCreateProject} disableRipple>
              New project
            </Button>
          </div>

          <div className="pl-stats">
            <div className="pl-stat pl-tone--blue" style={{ '--d': '0ms' }}>
              <span className="pl-stat__icon"><FolderCopyRoundedIcon /></span>
              <span className="pl-stat__text">
                <strong>{counts.total}</strong>
                <span>Projects</span>
              </span>
            </div>
            <div className="pl-stat pl-tone--green" style={{ '--d': '50ms' }}>
              <span className="pl-stat__icon"><BoltRoundedIcon /></span>
              <span className="pl-stat__text">
                <strong>{counts.active}</strong>
                <span>Active</span>
              </span>
            </div>
            <div className="pl-stat pl-tone--teal" style={{ '--d': '100ms' }}>
              <span className="pl-stat__icon"><DirectionsRunRoundedIcon /></span>
              <span className="pl-stat__text">
                <strong>{counts.sprinting}</strong>
                <span>Sprinting now</span>
              </span>
            </div>
            <div className={`pl-stat ${counts.atRisk ? 'pl-tone--red' : 'pl-tone--slate'}`} style={{ '--d': '150ms' }}>
              <span className="pl-stat__icon"><WarningAmberRoundedIcon /></span>
              <span className="pl-stat__text">
                <strong>{counts.atRisk}</strong>
                <span>At risk</span>
              </span>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ projects card */}
        <section className="pl-card">
          <div className={`pl-toolbar ${selectedCount ? 'has-selection' : ''}`}>
            {/* with projects ticked, the comparison is the next step — it leads */}
            {selectedCount ? (
              <div className="pl-toolbar__selection">
                <Button
                  className="pl-btn pl-btn--accent pl-btn--lead"
                  startIcon={<InsightsRoundedIcon />}
                  onClick={handleViewReports}
                  disableRipple
                >
                  View reports ({selectedCount})
                </Button>
                <span className="pl-selection">
                  <strong>{selectedCount}</strong> selected
                  <button type="button" onClick={() => setSelectedProjectIds([])}>
                    Clear
                  </button>
                </span>
              </div>
            ) : null}
            <label className={`pl-search ${searchTerm ? 'has-value' : ''}`}>
              <SearchRoundedIcon className="pl-search__icon" />
              <input
                ref={searchRef}
                type="search"
                value={searchTerm}
                placeholder="Search projects, keys or owners…"
                aria-label="Search projects"
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setSearchTerm('');
                }}
              />
              {searchTerm ? (
                <button
                  type="button"
                  className="pl-search__clear"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchTerm('');
                    searchRef.current?.focus();
                  }}
                >
                  <CloseRoundedIcon />
                </button>
              ) : null}
            </label>

            <div className="pl-seg" role="tablist" aria-label="Filter by status" data-active={statusFilter}>
              <span className="pl-seg__thumb" aria-hidden="true" />
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === filter.key}
                  className={statusFilter === filter.key ? 'is-active' : ''}
                  onClick={() => setStatusFilter(filter.key)}
                >
                  {filter.label}
                  <span className="pl-seg__count">{counts[filter.key === 'all' ? 'total' : filter.key]}</span>
                </button>
              ))}
            </div>

            <div className="pl-view" role="group" aria-label="Layout">
              {VIEW_MODES.map((mode) => (
                <Tooltip key={mode.key} title={mode.label}>
                  <button
                    type="button"
                    className={view === mode.key ? 'is-active' : ''}
                    aria-pressed={view === mode.key}
                    aria-label={mode.label}
                    onClick={() => changeView(mode.key)}
                  >
                    {mode.icon}
                  </button>
                </Tooltip>
              ))}
            </div>

            {!selectedCount ? (
              <div className="pl-toolbar__end">
                <span className="pl-hint">Tick projects to compare them</span>
                <Tooltip title="Select one or more projects to view reports">
                  <span className="pl-reports-wrap">
                    <Button className="pl-btn pl-btn--accent" startIcon={<InsightsRoundedIcon />} disabled disableRipple>
                      View reports
                    </Button>
                  </span>
                </Tooltip>
              </div>
            ) : null}
          </div>

          <JiraProjectsTable
            projects={projects}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            selectedIds={selectedProjectIds}
            setSelectedProjectIds={setSelectedProjectIds}
            onCreateProject={openCreateProject}
            view={view}
            loading={status === 'loading' && !projects.length}
          />
        </section>
      </div>
      <Footer />
      {issueModel && <CreateBacklogIssue isCreateNewBackIssuse={issueModel} setIsCreateNewBackIssue={setIssueModel} />}
    </div>
  );
};

export default Index;
